import React, { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { tenantsApi } from '../services/api';

function applyTheme(theme) {
    if (!theme) return;
    const root = document.documentElement;
    if (!root) return;
    if (theme.primary) root.style.setProperty('--primary', theme.primary);
    if (theme.primary700) root.style.setProperty('--primary-700', theme.primary700);
    if (theme.darkBtn) root.style.setProperty('--dark-btn', theme.darkBtn);
}

const navSections = [
    {
        title: 'Main',
        items: [
            { path: '/admin', label: 'Dashboard', icon: '◫' },
            { path: '/admin/profile', label: 'Profile', icon: '◉' },
        ],
    },
    {
        title: 'Inventory',
        items: [
            { path: '/admin/inventory', label: 'Products', icon: '◈' },
            { path: '/admin/inventory/create', label: 'Create Product', icon: '⊞' },
            { path: '/admin/inventory/expired', label: 'Expired Products', icon: '◌' },
            { path: '/admin/inventory/low-stock', label: 'Low Stocks', icon: '⌁' },
            { path: '/admin/inventory/category', label: 'Category', icon: '▦' },
            { path: '/admin/inventory/sub-category', label: 'Sub Category', icon: '▣' },
            { path: '/admin/inventory/brands', label: 'Brands', icon: '◇' },
            { path: '/admin/inventory/units', label: 'Units', icon: '◍' },
            { path: '/admin/inventory/variants', label: 'Variant Attributes', icon: '⋄' },
            { path: '/admin/inventory/warranties', label: 'Warranties', icon: '⌂' },
            { path: '/admin/inventory/barcode', label: 'Print Barcode', icon: '≡' },
            { path: '/admin/inventory/qrcode', label: 'Print QR Code', icon: '⌗' },
        ],
    },
    {
        title: 'Stock',
        items: [
            { path: '/admin/inventory/manage-stock', label: 'Manage Stock', icon: '▤' },
            { path: '/admin/inventory/adjustment', label: 'Stock Adjustment', icon: '↕' },
            { path: '/admin/inventory/transfer', label: 'Stock Transfer', icon: '⇄' },
        ],
    },
    {
        title: 'Sales',
        items: [
            { path: '/admin/pos/cashier', label: 'Cashier / POS', icon: '◧' },
            { path: '/admin/pos', label: 'POS (Classic)', icon: '⊡' },
            { path: '/admin/orders', label: 'Orders', icon: '▤' },
            { path: '/admin/pos/loyalty', label: 'Loyalty Program', icon: '★' },
            { path: '/admin/reports', label: 'Reports', icon: '△' },
        ],
    },
];

const masterNavSections = [
    {
        title: 'Master Panel',
        items: [
            { path: '/admin/master/control', label: 'Control Center', icon: '◆' },
            { path: '/admin/master/monitor', label: 'Server Monitor', icon: '◌' },
            { path: '/admin/master/billing', label: 'Billing & Payments', icon: '◈' },
            { path: '/admin/master-licences', label: 'Licences & Installer', icon: '◍' },
            { path: '/admin/profile', label: 'Profile', icon: '◉' },
        ],
    },
];

function UserAvatar({ user }) {
    const initials = `${user?.first_name?.[0] || user?.firstName?.[0] || 'U'}${user?.last_name?.[0] || user?.lastName?.[0] || ''}`.toUpperCase();
    return <div className="user-avatar">{initials}</div>;
}

export default function Layout() {
    const { user, logout, isMasterDeployment } = useAuth();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [tenantName, setTenantName] = useState(user?.tenant_name || user?.tenantName || 'Store');
    const isMasterAdmin = user?.role === 'super_admin' && isMasterDeployment;
    const isSalesRole = ['cashier', 'staff'].includes(user?.role);
    const displayName = user?.first_name || user?.firstName || 'User';
    const breadcrumbLabel = location.pathname === '/admin/inventory'
        ? 'Products'
        : location.pathname === '/admin/master/control'
            ? 'Control Center'
            : location.pathname === '/admin/master/monitor'
                ? 'Server Monitor'
                : location.pathname === '/admin/master/billing'
                    ? 'Billing & Payments'
                    : location.pathname === '/admin/master-licences'
                        ? 'Licences & Installer'
                        : location.pathname === '/admin'
                            ? 'Dashboard'
                            : location.pathname.split('/').filter(Boolean).slice(-1)[0]?.replace(/-/g, ' ') || 'Dashboard';
    const quickLinks = useMemo(() => [
        { to: '/admin/inventory/create', label: 'Add Product' },
        { to: '/admin/inventory/category', label: 'Add Category' },
        { to: '/admin/inventory/sub-category', label: 'Add Sub Category' },
        { to: '/admin/inventory/brands', label: 'Add Brand' },
        { to: '/admin/inventory/units', label: 'Add Unit' },
        { to: '/admin/inventory/variants', label: 'Add Variant' },
        { to: '/admin/inventory/warranties', label: 'Add Warranty' },
    ], []);

    const sections = useMemo(() => {
        if (isMasterAdmin) {
            return masterNavSections;
        }

        if (isSalesRole) {
            const salesSection = navSections.filter((section) => section.title === 'Sales').map((section) => ({
                ...section,
                items: section.items.filter((item) => ['/admin/pos/cashier', '/admin/pos'].includes(item.path)),
            }));
            return [
                { title: 'Main', items: [{ path: '/admin/profile', label: 'Profile', icon: '◉' }] },
                ...salesSection,
            ];
        }

        const base = [...navSections];
        if (user?.role === 'admin') {
            base.push({
                title: 'Administration',
                items: [
                    { path: '/admin/stores', label: 'Stores & Branches', icon: '▦' },
                    { path: '/admin/staff', label: 'Sales Staff', icon: '☰' },
                    { path: '/admin/settings', label: 'App Settings', icon: '⚙' },
                ],
            });
        } else if (user?.role === 'manager') {
            base.push({
                title: 'Administration',
                items: [
                    { path: '/admin/stores', label: 'Stores & Branches', icon: '▦' },
                    { path: '/admin/settings', label: 'App Settings', icon: '⚙' },
                ],
            });
        }
        return base;
    }, [isMasterAdmin, isSalesRole, user?.role]);

    useEffect(() => {
        if (!user || isMasterAdmin) {
            if (isMasterAdmin) {
                setTenantName('Master Platform');
            }
            return;
        }
        tenantsApi.me().then((res) => {
            if (res.success && res.data?.name) setTenantName(res.data.name);
            if (res.success && res.data?.metadata?.theme) applyTheme(res.data.metadata.theme);
        });
    }, [isMasterAdmin, user]);

    const openFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen?.();
            return;
        }
        document.exitFullscreen?.();
    };

    return (
        <div className="app-shell">
            <aside className="sidebar">
                <div className="brand brand-strong">
                    <div className="brand-logo-mark">D</div>
                    <div>
                        <h2>Royaltech <span>{isMasterAdmin ? 'MASTER' : 'POS'}</span></h2>
                        <p>{isMasterAdmin ? 'Store network command center' : 'Retail operations suite'}</p>
                    </div>
                </div>

                <div className="sidebar-scroll">
                    {sections.map((section) => (
                        <div key={section.title} className="nav-section">
                            <p className="nav-section-title">{section.title}</p>
                            <nav className="nav-links">
                                {section.items.map((item) => {
                                    const isActive = item.path === '/admin/inventory'
                                        ? location.pathname === '/admin/inventory'
                                        : location.pathname.startsWith(item.path);
                                    return (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            className={`nav-link${isActive ? ' active' : ''}`}
                                        >
                                            <span className="nav-icon">{item.icon}</span>
                                            <span>{item.label}</span>
                                            <span className="nav-chevron">›</span>
                                        </Link>
                                    );
                                })}
                            </nav>
                        </div>
                    ))}
                </div>
            </aside>

            <main className="main-content">
                <header className="topbar">
                    <div className="topbar-search-wrap">
                        <div className="topbar-search">
                            <span className="topbar-search-icon">⌕</span>
                            <input placeholder="Search" aria-label="Search" />
                            <span className="topbar-search-shortcut">⌘ K</span>
                        </div>
                    </div>
                    <div className="topbar-actions">
                        <div className="topbar-store-pill">
                            <span className="topbar-store-avatar">◉</span>
                            <span>{isMasterAdmin ? 'Master Platform' : tenantName}</span>
                        </div>
                        {!isSalesRole && !isMasterAdmin ? (
                            <div className="topbar-menu-wrap">
                                <button className="btn" type="button" onClick={() => setMenuOpen((value) => !value)}>Add New</button>
                                {menuOpen ? (
                                    <div className="topbar-dropdown-menu">
                                        {quickLinks.map((item) => (
                                            <Link key={item.to} to={item.to} onClick={() => setMenuOpen(false)}>{item.label}</Link>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        {!isMasterAdmin ? <Link to="/admin/pos" className="btn btn-dark">POS</Link> : null}
                        <button className="icon-btn" type="button" aria-label="Fullscreen" onClick={openFullscreen}>⤢</button>
                        {!isSalesRole && !isMasterAdmin ? <Link className="icon-btn" to="/admin/settings" aria-label="Settings">⚙</Link> : null}
                        <Link className="icon-btn" to="/admin/profile" aria-label="Profile">👤</Link>
                        <div className="topbar-user">
                            <UserAvatar user={user} />
                            <div className="topbar-user-meta">
                                <strong>{displayName}</strong>
                                <span>{user?.role || 'admin'}</span>
                            </div>
                        </div>
                        <button onClick={logout} className="btn btn-outline">Logout</button>
                    </div>
                </header>

                <section className="page-body">
                    <div className="shell-breadcrumbs">
                        <span>Dashboard</span>
                        <span className="crumb-sep">›</span>
                        <span>{breadcrumbLabel}</span>
                    </div>
                    <Outlet />
                </section>
            </main>
        </div>
    );
}
